import React, { useEffect, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TablePagination from '@mui/material/TablePagination'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import { differenceInCalendarDays, parseISO, subDays } from 'date-fns'
import palette from '../../../theme/palette'
import { useGetDispatchesQuery, useGetLiveQuery, useGetRunsQuery } from '../../redux/internalAlertsApiSlice'
import HistoryFilters from './HistoryFilters'
import { OutcomeBadge } from './HealthBadge'
import { LIVE_MAX_DAYS, channelOf, count, dayStr, deliveryOf, downloadRowsAsCsv, headerLabel, recipientAddress, recipientLabel, shortTime } from './alertUtils'
import { BAR_BG, LINE, MUTED, Th, btnSx, cardSx } from './ui'

const defaultFilters = () => ({ templateNames: [], from: dayStr(subDays(new Date(), 6)), to: dayStr(new Date()), status: '', currentStatus: '', mobile: '' })

// Read / Delivered / Pending are WhatsApp receipt states; Email / SMS runs only ever have Sent and Failed.
const RUN_COLS = [
  { key: 'startedAt', label: 'Time (IST)' }, { key: 'displayName', label: 'Alert' },
  { key: 'messages', label: 'Messages', num: true }, { key: 'recipients', label: 'Recipients', num: true },
  { key: 'read', label: 'Read', num: true }, { key: 'delivered', label: 'Delivered', num: true }, { key: 'sent', label: 'Sent', num: true },
  { key: 'pending', label: 'Pending', num: true }, { key: 'failed', label: 'Failed', num: true },
]
const msgCols = (channel) => [
  { key: 'sentAt', label: 'Sent (IST)' }, { key: 'displayName', label: 'Alert' }, { key: 'recipient', label: 'Recipient' },
  { key: 'outcome', label: 'Outcome' }, { key: 'errorSummary', label: 'Error' }, { key: 'renderedText', label: channel === 'EMAIL' ? 'Subject' : 'Message' },
]

/** Marks a send-test: kept in history, never counted by health or the 14-day strip. */
function TestChip() {
  return (
    <Tooltip title='Sent with "Send test" — not part of the scheduled alert, not counted in health' arrow>
      <Chip label='Test' size='small' variant='outlined' sx={{ ml: 0.75, height: 18, fontSize: '0.65rem', fontWeight: 700, color: palette.warning.dark, borderColor: palette.warning.main }} />
    </Tooltip>
  )
}

/** One message row, shared by the flat view, the expanded run and the live view. */
function MessageRow({ r, nameOf, onOpenAlert, showAlert = true }) {
  const [open, setOpen] = useState(false)
  const outcome = deliveryOf(r)
  const ch = channelOf(r)
  const receipt = r.readAt ? `Read ${shortTime(r.readAt)}` : r.deliveredAt ? `Delivered ${shortTime(r.deliveredAt)}`
    : ch === 'EMAIL' ? (r.status === 'failed' ? 'SES refused the mail' : 'Handed to Amazon SES (no read receipts for mail)')
    : ch === 'SMS' ? (r.status === 'failed' ? 'SMS gateway refused the message' : 'Handed to the SMS gateway (no receipts)')
    : r.currentStatus ? r.currentStatus : (r.status === 'failed' ? 'Provider rejected the message' : 'Accepted by the WhatsApp service, no receipt yet')
  const text = r.renderedText || (r.variables || []).join(' | ') || '—'
  return (
    <TableRow hover sx={{ '& td': { verticalAlign: 'top' } }}>
      <TableCell title={`${r.sentAt} IST`}>{shortTime(r.sentAt)}{r.test && <TestChip />}</TableCell>
      {showAlert && (
        <TableCell>
          <Box component='button' type='button' onClick={() => onOpenAlert?.(r.templateName)}
            sx={{ all: 'unset', cursor: 'pointer', color: palette.primary.main, fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
            {nameOf(r.templateName)}
          </Box>
          {r.integration && <Typography variant='caption' sx={{ display: 'block', color: 'text.disabled' }}>{r.integration}</Typography>}
        </TableCell>
      )}
      <TableCell>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {recipientLabel(r)}
          {ch === 'EMAIL' && r.headerType && r.headerType !== 'TO' && <Chip label={headerLabel(r.headerType)} size='small' variant='outlined' sx={{ ml: 0.75, height: 16, fontSize: '0.6rem', fontWeight: 700 }} />}
        </Typography>
        {r.displayName && <Typography variant='caption' sx={{ display: 'block', color: 'text.disabled', fontFamily: 'monospace' }}>{recipientAddress(r)}</Typography>}
      </TableCell>
      <TableCell><OutcomeBadge outcome={outcome} title={receipt} /></TableCell>
      <TableCell sx={{ color: palette.error.dark, maxWidth: 220, whiteSpace: 'normal' }}>{r.errorSummary || <Box component='span' sx={{ color: 'text.disabled' }}>—</Box>}</TableCell>
      <TableCell sx={{ maxWidth: 520, whiteSpace: 'normal' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: open ? 'pre-wrap' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 480 }} title={open ? undefined : text}>
            {text}
          </Typography>
          {text.length > 80 && (
            <IconButton size='small' onClick={() => setOpen(o => !o)} sx={{ p: 0.25 }} aria-label={open ? 'Collapse message' : 'Expand message'}>
              {open ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
            </IconButton>
          )}
        </Box>
      </TableCell>
    </TableRow>
  )
}

/** Messages of one run, fetched on expand. */
function RunMessages({ runKey, channel, nameOf, onOpenAlert }) {
  const { data, isFetching, error } = useGetDispatchesQuery({ runKey, pageSize: 200 })
  if (isFetching) return <Box sx={{ p: 1 }}>{[...Array(3)].map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)}</Box>
  if (error) return <Alert severity='error' sx={{ m: 1 }}>{error?.data?.message || error?.message || 'Could not load this run.'}</Alert>
  const rows = data?.rows || []
  return (
    <Table size='small' sx={{ '& td, & th': { whiteSpace: 'nowrap', fontSize: '0.78rem' }, bgcolor: BAR_BG }}>
      <TableHead>
        <TableRow>{msgCols(channel).filter(c => c.key !== 'displayName').map(c => <Th key={c.key} sx={{ position: 'static', background: BAR_BG }}>{c.label}</Th>)}</TableRow>
      </TableHead>
      <TableBody>
        {rows.map(r => <MessageRow key={r.id} r={r} nameOf={nameOf} onOpenAlert={onOpenAlert} showAlert={false} />)}
        {!rows.length && <TableRow><TableCell colSpan={5} align='center' sx={{ py: 2, color: 'text.disabled' }}>No messages in this run</TableCell></TableRow>}
      </TableBody>
    </Table>
  )
}

function RunRow({ r, channel, nameOf, onOpenAlert }) {
  const [open, setOpen] = useState(false)
  const bad = r.failed > 0
  return (
    <>
      <TableRow hover onClick={() => setOpen(o => !o)} sx={{ cursor: 'pointer', '& td': { fontVariantNumeric: 'tabular-nums' } }}>
        <TableCell sx={{ width: 36, p: 0.5 }}><IconButton size='small' aria-label={open ? 'Collapse run' : 'Expand run'}>{open ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}</IconButton></TableCell>
        <TableCell title={`${r.startedAt} – ${r.endedAt} IST`}>{shortTime(r.startedAt)}</TableCell>
        <TableCell>
          <Box component='button' type='button' onClick={(e) => { e.stopPropagation(); onOpenAlert?.(r.templateName) }}
            sx={{ all: 'unset', cursor: 'pointer', color: palette.primary.main, fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
            {r.displayName || nameOf(r.templateName)}
          </Box>
          {r.test && <TestChip />}
        </TableCell>
        <TableCell align='right'><strong>{count(r.messages)}</strong></TableCell>
        <TableCell align='right'>{count(r.recipients)}</TableCell>
        <TableCell align='right'>{count(r.read)}</TableCell>
        <TableCell align='right'>{count(r.delivered)}</TableCell>
        <TableCell align='right'>{count(r.sent)}</TableCell>
        <TableCell align='right' sx={{ color: r.pending ? palette.warning.dark : undefined }}>{count(r.pending)}</TableCell>
        <TableCell align='right' sx={{ color: bad ? palette.error.dark : undefined, fontWeight: bad ? 700 : 400 }}>{count(r.failed)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={RUN_COLS.length + 1} sx={{ p: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit><Box sx={{ px: 4, py: 1 }}><RunMessages runKey={r.runKey} channel={channel} nameOf={nameOf} onOpenAlert={onOpenAlert} /></Box></Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

/**
 * Message history of one channel: every internal-alert message, grouped by run (one alert's fan-out in a 10-minute
 * window) or flat. WhatsApp reads the ingested copy of the delivery audit — "Look in audit DB" is the one direct read
 * (single alert, ≤ 31 days) for history beyond the copy. Email / SMS rows are written by the senders themselves.
 * `preset` = { templateNames?, runKey? } handed in from the Catalogue / Health tabs.
 */
export default function MessageHistoryTab({ defs = [], channel = 'WHATSAPP', preset, onPresetConsumed, onOpenAlert }) {
  const wa = channel === 'WHATSAPP'
  const [filters, setFilters] = useState(defaultFilters)
  const [view, setView] = useState('runs')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(50)
  const [mobileDebounced, setMobileDebounced] = useState('')
  const [liveArgs, setLiveArgs] = useState(null)

  useEffect(() => {
    if (!preset) return
    setFilters(f => ({ ...f, templateNames: preset.templateNames || f.templateNames }))
    setLiveArgs(null)
    setPage(0)
    onPresetConsumed?.()
  }, [preset, onPresetConsumed])

  useEffect(() => { const t = setTimeout(() => setMobileDebounced((filters.mobile || '').trim()), 400); return () => clearTimeout(t) }, [filters.mobile])

  const nameOf = useMemo(() => { const m = {}; defs.forEach(d => { m[d.id] = d.displayName || d.id }); return (t) => m[t] || t }, [defs])

  const query = useMemo(() => ({
    channel, templateNames: filters.templateNames, from: filters.from, to: filters.to,
    status: filters.status || undefined, currentStatus: filters.status === 'failed' ? undefined : (filters.currentStatus || undefined),
    recipient: mobileDebounced || undefined, page: page + 1, pageSize,
  }), [channel, filters, mobileDebounced, page, pageSize])
  const scopeKey = JSON.stringify({ ...query, page: undefined, pageSize: undefined, view })
  useEffect(() => { setPage(0) }, [scopeKey])

  const runs = useGetRunsQuery(query, { skip: view !== 'runs' || Boolean(liveArgs) })
  const msgs = useGetDispatchesQuery(query, { skip: view !== 'messages' || Boolean(liveArgs) })
  const live = useGetLiveQuery(liveArgs, { skip: !liveArgs })
  const active = liveArgs ? live : view === 'runs' ? runs : msgs
  const data = active.data
  const total = liveArgs ? (data?.rows?.length || 0) : (data?.total || 0)
  const rows = data?.rows || []

  const rangeDays = differenceInCalendarDays(parseISO(filters.to), parseISO(filters.from)) + 1
  const liveTemplate = wa && filters.templateNames.length === 1 ? filters.templateNames[0] : null
  const canLive = Boolean(liveTemplate) && rangeDays <= LIVE_MAX_DAYS
  const liveHint = !liveTemplate ? 'Select exactly one alert to look it up in the audit DB' : rangeDays > LIVE_MAX_DAYS ? `Narrow the range to ${LIVE_MAX_DAYS} days or fewer` : `Read ${nameOf(liveTemplate)} directly from the WhatsApp delivery log for these dates (≤ 500 rows)`

  const exportCsv = () => {
    if (view === 'runs' && !liveArgs) {
      downloadRowsAsCsv(rows.map(r => ({ ...r, displayName: r.displayName || nameOf(r.templateName) })), RUN_COLS, `INTERNAL_ALERT_RUNS_${filters.from}_${filters.to}.csv`)
    } else {
      const cols = wa
        ? [{ key: 'sentAt', label: 'Sent (IST)' }, { key: 'templateName', label: 'Template' }, { key: 'alert', label: 'Alert' },
          { key: 'displayName', label: 'Recipient' }, { key: 'mobile', label: 'Mobile' }, { key: 'status', label: 'Status' }, { key: 'currentStatus', label: 'Receipt' },
          { key: 'deliveredAt', label: 'Delivered at' }, { key: 'readAt', label: 'Read at' }, { key: 'errorSummary', label: 'Error' }, { key: 'renderedText', label: 'Message' }]
        : [{ key: 'sentAt', label: 'Sent (IST)' }, { key: 'templateName', label: 'Alert key' }, { key: 'alert', label: 'Alert' },
          { key: 'displayName', label: 'Recipient' }, { key: 'address', label: channel === 'EMAIL' ? 'Email' : 'Mobile' }, { key: 'headerType', label: 'Header' },
          { key: 'status', label: 'Status' }, { key: 'errorSummary', label: 'Error' }, { key: 'renderedText', label: channel === 'EMAIL' ? 'Subject' : 'Message' }]
      downloadRowsAsCsv(rows.map(r => ({ ...r, alert: nameOf(r.templateName), address: recipientAddress(r) })), cols, `INTERNAL_ALERT_${channel}_MESSAGES_${liveArgs ? 'LIVE_' : ''}${filters.from}_${filters.to}.csv`)
    }
  }

  const loading = active.isFetching
  const error = active.error

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <HistoryFilters filters={filters} defs={defs} channel={channel} view={view} disabled={Boolean(liveArgs)}
        onChange={(f) => { setFilters(f); setLiveArgs(null) }}
        onViewChange={(v) => { setView(v); setLiveArgs(null) }}
        onReset={() => { setFilters(defaultFilters()); setLiveArgs(null) }} />

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
          {liveArgs ? (
            <>
              <Chip size='small' color='warning' icon={<StorageOutlinedIcon />} label={`Live from audit DB · ${nameOf(liveArgs.templateName)} · ${liveArgs.from} – ${liveArgs.to}`} onDelete={() => setLiveArgs(null)} />
              {data?.truncated && <Chip size='small' variant='outlined' label='Capped at 500 rows — narrow the dates' sx={{ height: 22 }} />}
            </>
          ) : (
            <Typography sx={{ fontSize: '0.78rem', color: MUTED }}>
              {view === 'runs' ? 'One row per alert run; expand to see each recipient\'s message and receipt.' : 'Every message, newest first.'}
              {data?.from && ` Showing ${data.from} – ${data.to} (IST).`}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {!liveArgs && wa && (
            <Tooltip title={liveHint} arrow>
              <span>
                <Button size='small' variant='outlined' startIcon={<StorageOutlinedIcon />} disabled={!canLive}
                  onClick={() => setLiveArgs({ templateName: liveTemplate, from: filters.from, to: filters.to, limit: 500 })}
                  sx={{ ...btnSx, whiteSpace: 'nowrap' }}>
                  Look in audit DB
                </Button>
              </span>
            </Tooltip>
          )}
          <Button size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={exportCsv} disabled={loading || Boolean(error) || !rows.length} sx={{ ...btnSx, whiteSpace: 'nowrap' }}>
            CSV · {count(rows.length)} rows
          </Button>
        </Box>
      </Box>

      {error && <Alert severity='error'>{error?.data?.message || error?.message || 'Could not load message history.'}</Alert>}

      {loading ? (
        <Box sx={{ p: 2 }}>{[...Array(8)].map((_, i) => <Skeleton key={i} height={32} sx={{ mb: 0.5 }} />)}</Box>
      ) : !error && (
        <>
          <TableContainer sx={{ ...cardSx, overflowX: 'auto' }}>
            {view === 'runs' && !liveArgs ? (
              <Table size='small' sx={{ '& td': { whiteSpace: 'nowrap', fontSize: '0.8rem', borderBottom: `1px solid ${LINE}` } }}>
                <TableHead>
                  <TableRow>
                    <Th />
                    {RUN_COLS.map(c => <Th key={c.key} right={c.num}>{c.label}</Th>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => <RunRow key={r.runKey} r={r} channel={channel} nameOf={nameOf} onOpenAlert={onOpenAlert} />)}
                  {!rows.length && <TableRow><TableCell colSpan={RUN_COLS.length + 1} align='center' sx={{ py: 4, color: 'text.disabled' }}>No runs in this scope</TableCell></TableRow>}
                </TableBody>
              </Table>
            ) : (
              <Table size='small' sx={{ '& td': { whiteSpace: 'nowrap', fontSize: '0.8rem', borderBottom: `1px solid ${LINE}` } }}>
                <TableHead>
                  <TableRow>{msgCols(channel).map(c => <Th key={c.key}>{c.label}</Th>)}</TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(r => <MessageRow key={r.id} r={r} nameOf={nameOf} onOpenAlert={onOpenAlert} />)}
                  {!rows.length && <TableRow><TableCell colSpan={msgCols(channel).length} align='center' sx={{ py: 4, color: 'text.disabled' }}>{liveArgs ? 'Nothing in the audit DB for this alert and range' : 'No messages in this scope'}</TableCell></TableRow>}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {!liveArgs && (
            <TablePagination
              component='div' count={total} page={page} rowsPerPage={pageSize}
              onPageChange={(_, p) => setPage(p)}
              onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0) }}
              rowsPerPageOptions={[25, 50, 100, 200]}
            />
          )}
        </>
      )}
    </Box>
  )
}
